import {
  SET_HIDE_URL_STATUS,
  UPDATE_ACTIVE_REGIONS,
  UPDATE_ACTIVE_VIEW,
  UPDATE_BACKDROP_INDEX,
  UPDATE_CLINIQUES,
  UPDATE_CURRENT_CLINIQUE,
  UPDATE_CURRENT_PERSON_TYPE,
  UPDATE_CURRENT_REGION,
  UPDATE_CURRENT_STEP,
  UPDATE_CURRENT_TREATMENT,
  UPDATE_FORM_IS_SUBMITTING,
  UPDATE_LOCALE,
  UPDATE_MAP_ACTIVE_MARKER,
  UPDATE_OPTIONS,
  UPDATE_REGIONS,
  UPDATE_TREATMENTS,
} from 'components/core/state/actions'
import {
  CoreAction,
  ICoreState,
  IRegion,
  ISetHideUrlStatusPayload,
  ITreatment,
  IUpdateActiveRegionsPayload,
  IUpdateActiveViewPayload,
  IUpdateBackdropIndexPayload,
  IUpdateCliniquesPayload,
  IUpdateCurrentCliniquePayload,
  IUpdateCurrentPersonTypePayload,
  IUpdateCurrentRegionPayload,
  IUpdateCurrentStepPayload,
  IUpdateCurrentTreatmentPayload,
  IUpdateFormIsSubmittingPayload,
  IUpdateLocalePayload,
  IUpdateMapActiveMarkerPayload,
  IUpdateOptionsPayload,
  IUpdateRegionsPayload,
  IUpdateTreatmentsPayload,
  IWPClinique,
  IWPRegion,
  IWPTreatment,
} from 'components/core/types'

const urlParams = new URLSearchParams(window.location.search)
const initialState: ICoreState = {
  activeRegions: [],
  activeView: 'map',
  cliniques: [],
  currentStep: parseInt(urlParams.get('rafStep'), 10) || 1,
  currentClinique: parseInt(urlParams.get('rafClinique'), 10) || null,
  currentPersonType: parseInt(urlParams.get('rafPersonType'), 10) || 1,
  currentRegion: parseInt(urlParams.get('rafRegion'), 10) || null,
  currentTreatment: parseInt(urlParams.get('rafTreatment'), 10) || null,
  formIsSubmitting: false,
  hideUrl: false,
  locale: 'fr',
  mapActiveMarker: null,
  options: {
    urgency_link: '#',
    zawin_link: '#',
  },
  regions: [],
  backdropIndex: null,
  treatments: [],
}

/* eslint-disable default-param-last */
const cliniquesReducer = (currentState: ICoreState = initialState, action: CoreAction) => {
  switch (action.type) {
    case SET_HIDE_URL_STATUS: {
      const { status } = action.payload as ISetHideUrlStatusPayload
      return {
        ...currentState,
        hideUrl: status,
      }
    }

    case UPDATE_ACTIVE_REGIONS: {
      const { activeRegions: payloadRegions } = action.payload as IUpdateActiveRegionsPayload
      if (payloadRegions.length) {
        const parentRegions = payloadRegions
          .reduce((group, region: IRegion) => ({
            ...group,
            [region.parent]: [
              ...(group[region.parent] || []),
              region,
            ],
          }), {})
        const activeRegions = parentRegions[0]
          .map((region: IRegion) => ([
            region,
            ...parentRegions[region.id] ?? [],
          ]))
        return {
          ...currentState,
          activeRegions: activeRegions.flat(),
          currentRegion: currentState.currentRegion || activeRegions.flat().at(-1)?.id,
        }
      }
      return {
        ...currentState,
        activeRegions: payloadRegions,
        currentRegion: currentState.currentRegion || payloadRegions.at(-1)?.id,
      }
    }

    case UPDATE_ACTIVE_VIEW: {
      const { activeView } = action.payload as IUpdateActiveViewPayload
      return {
        ...currentState,
        activeView,
      }
    }

    case UPDATE_CLINIQUES: {
      const { cliniques } = action.payload as IUpdateCliniquesPayload
      return {
        ...currentState,
        cliniques: cliniques.map((clinique: IWPClinique) => ({
          id: clinique.id,
          link: clinique.link,
          title: clinique.title.rendered,
          region: clinique.region,
          acf: {
            marker: clinique.acf.marker,
            marker_image: clinique.acf.marker_image,
            banner_clinique: clinique.acf.banner_clinique,
            code_zawin: clinique.acf.code_zawin,
            address: clinique.acf.address,
            city: clinique.acf.city,
            email: clinique.acf.email,
            phone: clinique.acf.phone,
            post_code: clinique.acf.post_code,
            short_description: clinique.acf.short_description,
            soins: clinique.acf.soins,
            subtitle: clinique.acf.subtitle,
          },
          google_datas: clinique.google_datas,
        })),
      }
    }

    case UPDATE_CURRENT_CLINIQUE: {
      const { cliniqueId } = action.payload as IUpdateCurrentCliniquePayload
      return {
        ...currentState,
        currentClinique: cliniqueId,
      }
    }

    case UPDATE_CURRENT_PERSON_TYPE: {
      const { personTypeId } = action.payload as IUpdateCurrentPersonTypePayload
      return {
        ...currentState,
        currentPersonType: personTypeId,
      }
    }

    case UPDATE_CURRENT_REGION: {
      const { regionId } = action.payload as IUpdateCurrentRegionPayload
      return {
        ...currentState,
        currentRegion: regionId,
        mapActiveMarker: null,
      }
    }

    case UPDATE_CURRENT_STEP: {
      const { step } = action.payload as IUpdateCurrentStepPayload
      return {
        ...currentState,
        currentStep: step,
      }
    }

    case UPDATE_CURRENT_TREATMENT: {
      const { treatmentId } = action.payload as IUpdateCurrentTreatmentPayload
      return {
        ...currentState,
        currentTreatment: treatmentId,
      }
    }

    case UPDATE_FORM_IS_SUBMITTING: {
      const { formIsSubmitting } = action.payload as IUpdateFormIsSubmittingPayload
      return {
        ...currentState,
        formIsSubmitting,
      }
    }

    case UPDATE_LOCALE: {
      const { locale } = action.payload as IUpdateLocalePayload
      return {
        ...currentState,
        locale,
      }
    }

    case UPDATE_MAP_ACTIVE_MARKER: {
      const { mapActiveMarker } = action.payload as IUpdateMapActiveMarkerPayload
      return {
        ...currentState,
        mapActiveMarker,
      }
    }

    case UPDATE_OPTIONS: {
      const { options } = action.payload as IUpdateOptionsPayload
      return {
        ...currentState,
        options: {
          urgency_link: options.urgency_link,
          zawin_link: options.zawin_link,
        },
      }
    }

    case UPDATE_REGIONS: {
      const { regions } = action.payload as IUpdateRegionsPayload
      const sortedRegions = regions.map((region: IWPRegion) => ({
        id: region.id,
        name: region.name,
        parent: region.parent,
        order: parseInt(region.acf.order, 10) || 10,
      }))
        .sort(({ order: a }: IRegion, { order: b }: IRegion) => a - b)
      return {
        ...currentState,
        currentRegion: currentState.currentRegion ?? sortedRegions[0]?.id ?? null,
        regions: sortedRegions,
      }
    }

    case UPDATE_BACKDROP_INDEX: {
      const { backdropIndex } = action.payload as IUpdateBackdropIndexPayload
      return {
        ...currentState,
        backdropIndex,
      }
    }

    case UPDATE_TREATMENTS: {
      const { treatments } = action.payload as IUpdateTreatmentsPayload
      const sortedTreatments = treatments.map((treatment: IWPTreatment) => ({
        id: treatment.id,
        name: treatment.name,
        description: treatment.description,
        except_kids: treatment.acf.except_kids,
        order: parseInt(treatment.acf.order, 10) || 10,
      }))
        .sort(({ order: a }: ITreatment, { order: b }: ITreatment) => a - b)
      return {
        ...currentState,
        currentTreatment: currentState.currentTreatment ?? sortedTreatments[0]?.id ?? null,
        treatments: sortedTreatments,
      }
    }

    default: {
      return {
        ...currentState,
      }
    }
  }
}
export default cliniquesReducer
